import { AlertTriangle, Clock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Caption,
  HStack,
  NewButton,
  Paragraph,
  VStack,
  useToast,
} from '@eluve/components';
import {
  useImpersonation,
  useImpersonationTimeRemaining,
  useSession,
} from '@eluve/session-helpers';

export const ImpersonationExpiryMonitor = () => {
  const session = useSession();
  const {
    isImpersonating,
    isLoading,
    renewImpersonation,
    exitImpersonation,
    expiresAt,
    impersonationOriginUrl,
  } = useImpersonation();
  const toast = useToast();

  const [showModal, setShowModal] = useState(false);
  const [cannotRenew, setCannotRenew] = useState(false);
  const [autoExitCountdown, setAutoExitCountdown] = useState<number | null>(
    null,
  );

  const timeRemaining = useImpersonationTimeRemaining(expiresAt, 'short');

  const currentUserName = session.firstName
    ? `${session.firstName} ${session.lastName}`.trim()
    : session.email;

  const handleRenew = useCallback(async () => {
    try {
      await renewImpersonation();
      setShowModal(false);
      setCannotRenew(false);
    } catch {
      toast.error('Failed to renew impersonation');
      setCannotRenew(true);
    }
  }, [renewImpersonation, toast]);

  const handleExit = useCallback(async () => {
    try {
      await exitImpersonation(impersonationOriginUrl);
    } catch {
      toast.error('Failed to exit impersonation');
    }
  }, [exitImpersonation, impersonationOriginUrl, toast]);

  useEffect(() => {
    if (!isImpersonating || !expiresAt) {
      setShowModal(false);
      setCannotRenew(false);
      setAutoExitCountdown(null);
      return;
    }

    const checkExpiry = () => {
      const timeLeft = expiresAt.getTime() - Date.now();
      const isExpiringSoon = timeLeft < 2 * 60 * 1000;
      const isExpired = timeLeft <= 0;

      if (isExpiringSoon) {
        setShowModal(true);
        if (isExpired && autoExitCountdown === null) {
          setAutoExitCountdown(15);
        }
      } else {
        setShowModal(false);
        setAutoExitCountdown(null);
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 1000);
    return () => clearInterval(interval);
  }, [isImpersonating, expiresAt, autoExitCountdown]);

  useEffect(() => {
    if (cannotRenew && autoExitCountdown === null) {
      setAutoExitCountdown(15);
    }
  }, [cannotRenew, autoExitCountdown]);

  useEffect(() => {
    if (autoExitCountdown === null) return;

    if (autoExitCountdown === 0) {
      handleExit();
      return;
    }

    const timer = setTimeout(() => {
      setAutoExitCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [autoExitCountdown, handleExit]);

  const isExpired = expiresAt && expiresAt.getTime() <= Date.now();

  const title = isExpired
    ? 'Impersonation Session Expired'
    : 'Impersonation Session Expiring Soon';

  const timeRemainingDisplay = timeRemaining
    ? ` (${timeRemaining} remaining)`
    : 'soon';

  const description =
    isExpired || cannotRenew
      ? autoExitCountdown !== null
        ? `Your impersonation session has expired. Returning to admin view in ${autoExitCountdown} seconds...`
        : 'Your impersonation session has expired or cannot be renewed. Please exit impersonation to return to your admin account.'
      : `Your impersonation session will expire ${timeRemainingDisplay}. Would you like to renew the session or exit impersonation?`;

  return (
    <AlertDialog open={showModal}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            <HStack gap={2} className="items-center">
              <AlertTriangle className="size-5 text-text-yellow-strong" />
              {title}
            </HStack>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <VStack gap={3}>
              <Paragraph size="sm" color="secondary">
                {description}
              </Paragraph>

              <VStack className="rounded-md bg-bg-secondary p-3">
                <HStack gap={2} className="items-center">
                  <Caption size="sm" weight="semibold" color="tertiary">
                    Currently impersonating:
                  </Caption>
                </HStack>
                <Caption size="sm" color="primary">
                  <span className="font-semibold">{currentUserName}</span>
                  {currentUserName !== session.email && ` (${session.email})`}
                </Caption>

                {timeRemaining && !isExpired && (
                  <HStack gap={1} className="mt-2 items-center">
                    <Clock className="size-3 text-text-yellow-strong" />
                    <Caption size="sm" color="secondary">
                      {timeRemaining} remaining
                    </Caption>
                  </HStack>
                )}
              </VStack>
            </VStack>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {isExpired || cannotRenew ? (
            <NewButton
              text={
                isLoading
                  ? 'Exiting...'
                  : autoExitCountdown !== null
                    ? 'Return Now'
                    : 'Exit Impersonation'
              }
              type="primary"
              size="m"
              onClick={handleExit}
              disabled={isLoading}
              wFull
              icon={{ name: 'AlertTriangle' }}
            />
          ) : (
            <HStack gap={2} className="w-full">
              <NewButton
                text={isLoading ? 'Renewing...' : 'Renew Session'}
                type="outline"
                size="m"
                onClick={handleRenew}
                disabled={isLoading}
                wFull
                icon={isLoading ? undefined : { name: 'RefreshCw' }}
              />
              <NewButton
                text={isLoading ? 'Exiting...' : 'Exit Impersonation'}
                type="outline"
                size="m"
                onClick={handleExit}
                disabled={isLoading}
                wFull
                icon={{ name: 'AlertTriangle' }}
              />
            </HStack>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
